import React, { FC, useState, useEffect, useLayoutEffect, useRef, createContext } from "react";
import { useMachine } from "@xstate/react";
import Alert from "./Alert";
import { NotificationIntent } from "types/Notification";
import { transititionMachine } from "./transition.machine";

export const ToastContext = createContext(() => {});

function useTransition(isShown: boolean) {
  const isMounted = useRef(false);
  const [current, send] = useMachine(transititionMachine);

  useEffect(() => {
    if (isShown && current.matches("exited") && !isMounted.current) {
      send({ type: "enter" });
    }

    if (!isShown && (current.matches("entered") || (current as any).matches("entering"))) {
      send({ type: "exit" });
    }

    isMounted.current = true;
  }, [isShown]);

  return current.value;
}

const Toast: FC<
  React.PropsWithChildren<{
    title: string;
    intent: NotificationIntent;
    duration: number;
    onRemove(): void;
  }>
> = ({ title, intent, duration = 2, onRemove, children }) => {
  const [isShown, setShown] = useState(true);

  const state = useTransition(isShown);
  const ref = useRef<HTMLDivElement | null>(null);
  const timer = useRef<NodeJS.Timeout | null>(null);

  useEffect(() => {
    if (!isShown && state === "exited") {
      onRemove();
    }
  }, [state]);

  useEffect(() => {
    if (!localStorage.getItem("debug::notification")) {
      startTimer();
    }
    return () => {
      clearTimer();
    };
  }, []);

  function startTimer() {
    timer.current = setTimeout(() => {
      close();
    }, duration * 1000);
  }

  function clearTimer() {
    timer.current && clearTimeout(timer.current);
  }

  function onMouseEnter() {
    clearTimer();
  }

  function onMouseLeave() {
    startTimer();
  }

  function close() {
    clearTimer();
    setShown(false);
  }

  if (state === "exited") {
    return null;
  }

  return (
    <div
      data-state={state}
      className="toast"
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
      style={{
        zIndex: 50,
      }}
    >
      <div ref={ref} className="alert-container" style={{ padding: 8 }}>
        <Alert intent={intent} title={title} onRemove={() => close()}>
          <ToastContext.Provider value={close}>{children}</ToastContext.Provider>
        </Alert>
      </div>
    </div>
  );
};

Toast.defaultProps = {
  intent: "DEFAULT",
};

export default Toast;
